import React, { FC, useLayoutEffect } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { HeadProvider, Link, Title } from 'react-head'

import Claims from '../lib/claims'
import { SignInPage } from '../common/pages/sign-in-page'
import { PasswordResetPage } from '../common/pages/password-reset-page'
import { ResetPasswordRequestPage } from '../common/pages/reset-password-request-page'
import { ResetPasswordRequestSuccessPage } from '../common/pages/reset-password-request-success-page'
import { ResetPasswordRequestErrorPage } from '../common/pages/reset-password-request-error-page'
import { MeasurementDevicePage } from '../common/pages/measurement-device-page'
import { LoadbalancerPage } from '../common/pages/loadbalancer-page'
import { LoadBalancerDetailsWidgetPage } from '../common/components/load-balancer-details-widget-page'
import { ChargePointDetailsPage } from '../common/pages/chargepoint-details-page'
import { ReportsPage } from '../common/pages/reports-page'
import CookieConsentBar from '../common/features/cookie-consent/CookieConsentBar'
import { Company, currentCompany } from '../lib/current-company'
import { RFIDTagsPage } from '../common/pages/rfid-tags-page'

import AdminPageHeader from './components/PageHeader'
import { RootState } from './redux/store'
import { AcceptInvitationPage } from './pages/accept-invitation-page'
import { DashboardPage } from './pages/dashboard-page'
import { SelectOrganisationPage } from './pages/select-organisation-page'
import { PoolsPage } from './pages/pools-page'
import { PoolPage } from './pages/pool-page'
import { MeasurementDevicesPage } from './pages/measurementdevices-page'
import { UsersPage } from './pages/users-page'
import { UserPoolsPage } from './pages/user-pools-page'
import { UserChargePointsPage } from './pages/user-chargepoints-page'
import { UserNfcTagsPage } from './pages/user-nfc-tags-page'
import { AdminChargePointsPage } from './pages/chargepoints-page'
import { SettingsPage } from './pages/settings-page'
import { UserPage } from './pages/user-page'
import { ChangeOperatorPage } from './pages/change-operator-page/change-operator-page'
import { ChangeOperatorSuccessPage } from './pages/change-operator-success-page'

type RequireAuthProps = {
    children?: JSX.Element | React.ReactNode
    isHeaderShown?: boolean
}

const RequireAuth: FC<RequireAuthProps> = ({ children, isHeaderShown = true }) => {
    const location = useLocation()
    const token = useSelector((state: RootState) => state.token)
    const allowAccess = useSelector(
        (state: RootState) =>
            state.user.isSuperAdmin ||
            state.user.roles.some((r) => r.claim === Claims.Admin || r.claim === Claims.AdminViewOnly)
    )
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)

    if (!token || !allowAccess) {
        return <Navigate to="/login" state={{ from: location.pathname }} replace />
    }

    if (!companyId && location.pathname !== '/select-organisation') {
        return <Navigate to="/select-organisation" state={{ from: location.pathname }} replace />
    }

    return (
        <div>
            {isHeaderShown && <AdminPageHeader className="page-layout-header pt-5 pb-6 xl:pt-4 xl:pb-4" />}
            {children}
        </div>
    )
}

const App = () => {
    const isFortum = currentCompany === Company.FORTUM
    const token = useSelector((state: RootState) => state.token)

    const location = useLocation()

    useLayoutEffect(() => {
        // Scroll to the top of the page when the route changes
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    }, [location.pathname])

    return (
        <HeadProvider>
            <Title>{isFortum ? 'Fortum Admin' : 'Charge Amps Admin'}</Title>
            {isFortum && <Link rel="stylesheet" href="/common/content/customers/fortum/fortum.css" />}
            <Link
                rel="shortcut icon"
                href={
                    isFortum
                        ? '/common/content/customers/fortum/images/fortum_favicon.ico'
                        : '/common/content/images/favicon-16.png'
                }
            />
            <Link
                rel="apple-touch-icon"
                href={
                    isFortum
                        ? '/common/content/customers/fortum/images/fortum_logo_apple_touch_icon.png'
                        : '/common/content/images/logo_white_retina.png'
                }
            />
            <Routes>
                <Route path="/*" element={<Navigate to={token ? '/dashboard' : '/login'} />} />
                <Route path="login" element={<SignInPage />} />
                <Route path="reset-password" element={<PasswordResetPage />} />
                <Route path="acceptInvitation" element={<AcceptInvitationPage />} />
                <Route path="reset-password-request" element={<ResetPasswordRequestPage />} />
                <Route path="reset-password-request-success" element={<ResetPasswordRequestSuccessPage />} />
                <Route path="reset-password-request-error" element={<ResetPasswordRequestErrorPage />} />
                <Route
                    path="select-organisation"
                    element={
                        <RequireAuth isHeaderShown={false}>
                            <SelectOrganisationPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="change-operator/:transferKey"
                    element={
                        <RequireAuth isHeaderShown={false}>
                            <ChangeOperatorPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="change-operator-success"
                    element={
                        <RequireAuth isHeaderShown={false}>
                            <ChangeOperatorSuccessPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="dashboard"
                    element={
                        <RequireAuth>
                            <DashboardPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="pools"
                    element={
                        <RequireAuth>
                            <PoolsPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="pool/:poolId"
                    element={
                        <RequireAuth>
                            <PoolPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="measurementDevices"
                    element={
                        <RequireAuth>
                            <MeasurementDevicesPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="measurementDevice/:deviceId"
                    element={
                        <RequireAuth>
                            <MeasurementDevicePage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="loadbalancer"
                    element={
                        <RequireAuth>
                            <LoadbalancerPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="loadbalancer/:id"
                    element={
                        <RequireAuth>
                            <LoadBalancerDetailsWidgetPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="nfcTags"
                    element={
                        <RequireAuth>
                            <RFIDTagsPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="users"
                    element={
                        <RequireAuth>
                            <UsersPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="user"
                    element={
                        <RequireAuth>
                            <UserPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="user/:id"
                    element={
                        <RequireAuth>
                            <UserPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="user/:id/userPools"
                    element={
                        <RequireAuth>
                            <UserPoolsPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="user/:id/userChargePoints"
                    element={
                        <RequireAuth>
                            <UserChargePointsPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="user/:id/userNfcTags"
                    element={
                        <RequireAuth>
                            <UserNfcTagsPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="chargepoints"
                    element={
                        <RequireAuth>
                            <AdminChargePointsPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="chargepoints/:chargePointId"
                    element={
                        <RequireAuth>
                            <ChargePointDetailsPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="reports"
                    element={
                        <RequireAuth>
                            <ReportsPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="settings"
                    element={
                        <RequireAuth>
                            <SettingsPage />
                        </RequireAuth>
                    }
                />
            </Routes>
            <CookieConsentBar />
        </HeadProvider>
    )
}

export default App
